import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, Chip, CircularProgress, Alert,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Tabs, Tab, Paper, Autocomplete, Avatar, Stack, LinearProgress, IconButton,
  Tooltip, Snackbar, Switch, FormControlLabel, Table, TableHead, TableRow,
  TableCell, TableBody, Badge, ToggleButtonGroup, ToggleButton,
  Stepper, Step, StepLabel, Checkbox, FormGroup, Divider, Radio, RadioGroup,
  InputAdornment,
} from '@mui/material';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PetsIcon from '@mui/icons-material/Pets';
import SearchIcon from '@mui/icons-material/Search';
import GridViewIcon from '@mui/icons-material/GridView';
import ListIcon from '@mui/icons-material/List';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import WarningIcon from '@mui/icons-material/Warning';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { onSnapshot, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { getCollection, addDocument, updateDocument } from '../services/firestoreService';
import { syncEgresoHospitalario } from '../services/egresosService';
import { crearPresupuestoAutomatico } from '../services/presupuestoAutoService';
import PatientSearch from '../components/PatientSearch';
import { COLORS } from '../theme/constants';
import { CLP } from '../utils/finance';
import { useBranch } from '../context/BranchContext';
import { useAuth } from '../context/AuthContext';

// ─── Admission stepper constants ─────────────────────────────────────────────
const SINTOMAS_OPC = ['Vómitos','Diarrea','Letargia','Anorexia','Poliuria','Polidipsia','Tos','Estornudos','Disnea','Convulsiones','Colapso'];
const SISTEMAS_OPC = ['General/apariencia','Ojos','Oídos','Nariz/senos','Cavidad oral','Linfonodos','Cardiovascular','Respiratorio','Abdomen','Gastrointestinal','Urogenital','Musculoesquelético','Neurológico','Piel/tegumento'];
const ADM_STEPS    = ['Paciente & Propietario','Historia Clínica','Examen Físico','Plan & Consentimiento'];

const { primary: PRIMARY, teal: TEAL, warning: AMBER, danger: DANGER, success: SUCCESS, info: INFO } = COLORS;

const CLINICAL_STATUS_CONFIG = {
  estable:      { label: 'Estable',        bg: '#D1FAE5', color: '#065F46', border: '#10B981' },
  alerta:       { label: 'En observación', bg: '#FEF3C7', color: '#92400E', border: '#F59E0B' },
  critico:      { label: 'Crítico',        bg: '#FEE2E2', color: '#991B1B', border: '#DC2626', pulse: true },
  recuperacion: { label: 'Recuperación',   bg: '#DBEAFE', color: '#1E40AF', border: '#3B82F6' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const toDate = (v) => !v ? null : (typeof v?.toDate === 'function' ? v.toDate() : new Date(v));

const calcDiasInternado = (admissionAt) => {
  const d = toDate(admissionAt);
  if (!d) return 0;
  return Math.max(1, Math.ceil((new Date() - d) / 86400000));
};

const calcTreatmentStatus = (nextTreatmentAt, now = new Date(), name = '') => {
  if (!nextTreatmentAt) return { isOverdue: false, isDue: false, minutesUntil: null, label: 'Sin tratamientos', chipColor: '#E2E8F0', chipTextColor: '#64748B' };
  const t = toDate(nextTreatmentAt);
  const diff = Math.round((t - now) / 60000);
  const timeStr = t.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
  const prefix = name ? `${name} ${timeStr}` : timeStr;
  if (diff < 0)  return { isOverdue: true,  isDue: false, minutesUntil: diff, label: `${prefix} · ${Math.abs(diff)} min atrasado`, chipColor: '#FEE2E2', chipTextColor: '#991B1B' };
  if (diff <= 30) return { isOverdue: false, isDue: true, minutesUntil: diff, label: `${prefix} · Próx. ${diff} min`, chipColor: '#FEF3C7', chipTextColor: '#92400E' };
  return { isOverdue: false, isDue: false, minutesUntil: diff, label: `${prefix} · Al día`, chipColor: '#D1FAE5', chipTextColor: '#065F46' };
};

const getOccupancyColor = (pct) => pct > 90 ? DANGER : pct > 70 ? AMBER : SUCCESS;

const getSpeciesColor = (species) => {
  const s = (species || '').toLowerCase();
  if (s.includes('canino') || s.includes('perro')) return '#FF9800';
  if (s.includes('felino') || s.includes('gato'))  return '#AB47BC';
  return '#42A5F5';
};


// ─── CageCard ─────────────────────────────────────────────────────────────────
const CageCard = ({ resourceName, hospitalization, patient, onAdmit, onOpenFlowsheet, onOpenRecord, onDischarge, onMarkCleaned, cleaningHosp, tvMode, now }) => {
  const ultimaAlerta = useMemo(() => {
    const notas = (hospitalization?.notasTurno || []).filter(n => n.alertasParaSiguienteTurno?.trim());
    if (!notas.length) return null;
    return notas.sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0))[0];
  }, [hospitalization?.notasTurno]);

  if (!hospitalization && cleaningHosp) {
    return (
      <Card sx={{
        borderRadius: 3, border: '2px solid #F59E0B', bgcolor: '#FFFBEB',
        minHeight: tvMode ? 160 : 120,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all .2s',
      }}>
        <CardContent sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant={tvMode ? 'body1' : 'body2'} fontWeight={700} color="text.secondary">{resourceName}</Typography>
          <Typography variant="caption" sx={{ color: '#92400E', display: 'block', mt: 0.5 }}>🧹 Pendiente de limpieza</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {cleaningHosp.patientName || 'Paciente'}
          </Typography>
          <Button size="small" variant="outlined" sx={{ mt: 1, color: '#92400E', borderColor: '#F59E0B', fontSize: '0.72rem' }}
            startIcon={<CheckCircleIcon sx={{ fontSize: '0.85rem!important' }}/>}
            onClick={() => onMarkCleaned(cleaningHosp)}>Marcar limpia</Button>
        </CardContent>
      </Card>
    );
  }

  if (!hospitalization) {
    return (
      <Card onClick={onAdmit} sx={{
        borderRadius: 3, border: '2px dashed #D1D5DB', bgcolor: '#FAFAFA',
        cursor: 'pointer', minHeight: tvMode ? 160 : 120,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all .2s', '&:hover': { borderColor: TEAL, bgcolor: '#F0FDFA', boxShadow: 2 },
      }}>
        <CardContent sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant={tvMode ? 'body1' : 'body2'} fontWeight={700} color="text.secondary">{resourceName}</Typography>
          <Typography variant="caption" sx={{ color: SUCCESS, display: 'block', mt: 0.5 }}>● Disponible</Typography>
          <Button size="small" sx={{ mt: 1, color: TEAL, fontSize: '0.72rem' }} startIcon={<AddIcon sx={{ fontSize: '0.85rem!important' }}/>}>Ingresar</Button>
        </CardContent>
      </Card>
    );
  }

  const st = CLINICAL_STATUS_CONFIG[hospitalization.clinicalStatus] || CLINICAL_STATUS_CONFIG.estable;
  const isCritical = hospitalization.clinicalStatus === 'critico';
  const dias = calcDiasInternado(hospitalization.admissionAt);
  const ttoSt = calcTreatmentStatus(hospitalization.nextTreatmentAt, now, hospitalization.nextTreatmentName || '');
  const speciesColor = getSpeciesColor(hospitalization.patientSpecies);

  return (
    <Card sx={{
      borderRadius: 3, border: `2px solid ${st.border}`, position: 'relative',
      bgcolor: isCritical ? '#FFF5F5' : 'white', minHeight: tvMode ? 280 : 200,
      '@keyframes criticalPulse': {
        '0%, 100%': { borderColor: '#DC2626', boxShadow: '0 0 0 0 rgba(220,38,38,.2)' },
        '50%':       { borderColor: '#EF4444', boxShadow: '0 0 0 4px rgba(220,38,38,0)' },
      },
      animation: isCritical ? 'criticalPulse 2s ease-in-out infinite' : 'none',
    }}>
      {/* Barra lateral */}
      <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: st.border, borderRadius: '3px 0 0 3px' }}/>

      <CardContent sx={{ pl: 2.5, pr: 1.5, pt: 1.5, pb: '8px!important' }}>
        {/* Header: jaula + estado */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary">{resourceName}</Typography>
          <Chip label={st.label} size="small" sx={{ bgcolor: st.bg, color: st.color, fontSize: '0.6rem', height: 18, fontWeight: 700 }}/>
        </Box>

        {/* Avatar + paciente */}
        <Box sx={{ display: 'flex', gap: 1, mb: 0.8, alignItems: 'flex-start' }}>
          <Avatar src={patient?.photoURL} sx={{ width: 34, height: 34, bgcolor: speciesColor, flexShrink: 0, fontSize: '0.9rem' }}>
            <PetsIcon sx={{ fontSize: 18 }}/>
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant={tvMode ? 'body1' : 'body2'} fontWeight={700} sx={{ whiteSpace: 'normal', lineHeight: 1.2 }}>
              {patient?.name || hospitalization.patientName || 'Paciente'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ whiteSpace: 'normal', lineHeight: 1.3 }}>
              {hospitalization.patientSpecies} · {hospitalization.patientBreed || '–'} · {patient?.weight || hospitalization.patientWeight || '?'} kg
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ whiteSpace: 'normal', lineHeight: 1.3 }}>
              {hospitalization.ownerName || 'Propietario N/D'}
            </Typography>
            {hospitalization.ownerConvenio && (
              <Chip label={hospitalization.ownerConvenio} size="small" color="info" variant="outlined"
                sx={{ fontSize: '0.6rem', height: 'auto', minHeight: 18, mt: 0.4, maxWidth: '100%', '& .MuiChip-label': { whiteSpace: 'normal', lineHeight: 1.25, py: 0.3 } }} />
            )}
          </Box>
          <Chip label={`Día ${dias}`} size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF', fontSize: '0.6rem', height: 18, flexShrink: 0 }}/>
        </Box>

        {/* Diagnóstico */}
        {hospitalization.primaryDiagnosis && (
          <Chip label={hospitalization.primaryDiagnosis} size="small" variant="outlined"
            sx={{ fontSize: '0.62rem', mb: 0.5, maxWidth: '100%', height: 'auto', minHeight: 20, '& .MuiChip-label': { whiteSpace: 'normal', lineHeight: 1.25, py: 0.3 } }}/>
        )}

        {/* Alergias */}
        {(patient?.alergias || hospitalization.patientAlergias) && (
          <Box sx={{ p: '3px 6px', bgcolor: '#FEF2F2', borderRadius: 1, border: '1px solid #FECACA', mb: 0.5 }}>
            <Typography variant="caption" color="error.main" fontWeight={700} sx={{ fontSize: '0.65rem' }}>
              ⚠ {patient?.alergias || hospitalization.patientAlergias}
            </Typography>
          </Box>
        )}

        {/* Alerta de turno */}
        {ultimaAlerta && (
          <Box sx={{ p: '3px 6px', bgcolor: '#FFFBEB', borderRadius: 1, border: '1px solid #FDE68A', mb: 0.5 }}>
            <Typography variant="caption" color="warning.dark" fontWeight={700} sx={{ fontSize: '0.65rem' }}>
              ⚠ {ultimaAlerta.alertasParaSiguienteTurno}
            </Typography>
          </Box>
        )}

        {/* Últimos vitales */}
        {hospitalization.lastVitals && (
          <Stack direction="row" spacing={1} mb={0.5} flexWrap="wrap">
            {hospitalization.lastVitals.temperatura && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>T° {hospitalization.lastVitals.temperatura}°C</Typography>
            )}
            {hospitalization.lastVitals.frecuenciaCardiaca && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>FC {hospitalization.lastVitals.frecuenciaCardiaca}</Typography>
            )}
            {hospitalization.lastVitals.scoreDolor != null && (
              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: hospitalization.lastVitals.scoreDolor >= 3 ? DANGER : 'text.secondary' }}>
                Dolor {hospitalization.lastVitals.scoreDolor}/4
              </Typography>
            )}
          </Stack>
        )}

        {/* Estado de tratamiento */}
        <Box mb={tvMode ? 0 : 0.8}>
          <Chip
            icon={ttoSt.isOverdue ? <WarningIcon sx={{ fontSize: '0.7rem!important', color: ttoSt.chipTextColor + '!important' }}/> : null}
            label={ttoSt.label} size="small"
            sx={{ bgcolor: ttoSt.chipColor, color: ttoSt.chipTextColor, fontSize: '0.62rem', height: 'auto', minHeight: 20, fontWeight: 700, border: ttoSt.isOverdue ? `1px solid ${ttoSt.chipTextColor}` : 'none', '& .MuiChip-label': { whiteSpace: 'normal', lineHeight: 1.25, py: 0.3 } }}/>
        </Box>

        {/* Acciones */}
        {!tvMode && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            <Button size="small" variant="outlined" startIcon={<MedicalServicesIcon sx={{ fontSize: '0.8rem!important' }}/>}
              onClick={() => onOpenFlowsheet(hospitalization.id)}
              sx={{ fontSize: '0.62rem', py: 0.2, px: 0.8, borderColor: '#E2E8F0', color: PRIMARY }}>Ttos</Button>
            <Button size="small" variant="outlined" startIcon={<AssignmentIcon sx={{ fontSize: '0.8rem!important' }}/>}
              onClick={() => onOpenRecord(hospitalization.patientId)}
              sx={{ fontSize: '0.62rem', py: 0.2, px: 0.8, borderColor: '#E2E8F0', color: PRIMARY }}>Ficha</Button>
            <Button size="small" variant="contained" color="error"
              onClick={() => onDischarge(hospitalization)}
              sx={{ fontSize: '0.62rem', py: 0.2, px: 0.8, ml: 'auto!important' }}>Alta</Button>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

// ─── DischargeDialog ──────────────────────────────────────────────────────────
const DISCHARGE_CHECKLIST = [
  { key:'diagnosticoEgreso',    label:'Diagnóstico de egreso confirmado' },
  { key:'medicamentosPrescritos', label:'Medicamentos para casa prescritos' },
  { key:'instrucciones',        label:'Instrucciones de cuidado en casa' },
  { key:'restricciones',        label:'Restricciones de actividad especificadas' },
  { key:'dieta',                label:'Dieta de alta especificada' },
  { key:'control',              label:'Control de seguimiento agendado' },
  { key:'signosAlarma',         label:'Signos de alarma explicados al propietario' },
  { key:'propietarioConfirmo',  label:'Propietario confirmó recepción de instrucciones' },
];

const DischargeDialog = ({ open, onClose, hospitalization, onConfirm, saving }) => {
  const navigate = useNavigate();
  const [checks, setChecks] = React.useState({});
  const [notas, setNotas] = React.useState({});
  const [diagnosticoEgreso, setDiagnosticoEgreso] = React.useState('');
  React.useEffect(() => { if (open) { setChecks({}); setNotas({}); setDiagnosticoEgreso(''); } }, [open]);
  const dias = hospitalization ? calcDiasInternado(hospitalization.admissionAt) : '?';
  const allDone = DISCHARGE_CHECKLIST.every(i => checks[i.key]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: PRIMARY, fontWeight: 700 }}>
        Dar de Alta — {hospitalization?.patientName}
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Paciente internado <strong>{dias} día(s)</strong> en {hospitalization?.cageName}
        </Alert>
        <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
          Para un alta completa con epicrisis, instrucciones al propietario y firma, usa el módulo de Epicrisis.
        </Alert>
        <TextField label="Diagnóstico de egreso" fullWidth size="small" required
          value={diagnosticoEgreso}
          onChange={e => setDiagnosticoEgreso(e.target.value)}
          sx={{ mb: 2 }}/>
        <Stack spacing={1.5}>
          {DISCHARGE_CHECKLIST.map(item => (
            <Box key={item.key}>
              <FormControlLabel
                control={<Switch checked={!!checks[item.key]} onChange={e=>setChecks(p=>({...p,[item.key]:e.target.checked}))}/>}
                label={<Typography variant="body2" fontWeight={checks[item.key]?700:400}>{item.label}</Typography>}/>
              {checks[item.key] && ['instrucciones','restricciones','dieta','control','signosAlarma'].includes(item.key) && (
                <TextField size="small" fullWidth placeholder="Detalle..." value={notas[item.key]||''}
                  onChange={e=>setNotas(p=>({...p,[item.key]:e.target.value}))} sx={{ mt:0.5, ml:4 }}/>
              )}
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="outlined" color="primary"
          onClick={()=>{ onClose(); navigate(`/hospital-epicrisis/${hospitalization?.id}`); }}>
          📋 Epicrisis completa
        </Button>
        <Button variant="contained" color="error" onClick={() => onConfirm({ diagnosticoEgreso, checks, notas })} disabled={saving || !diagnosticoEgreso.trim() || !allDone}>
          {saving ? 'Procesando…' : 'Alta rápida'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── AdmissionStepperDialog ───────────────────────────────────────────────────
const INIT_FORM = {
  patientId:'', pesoAlIngreso:'', motivoIngreso:'', tipoIngreso:'urgencia',
  clinicalStatus:'estable', veterinarioId:'', isInfectious:false,
  diasEstimados:'', primaryDiagnosis:'', diagnosisId:'', notes:'',
  tiempoEvolucion:'', sintomas:[], tratamientoPrevio:false, medicacionPrevia:'',
  hospitalizacionesPrevias:false, notasHospitalizaciones:'', cirugiasPrevias:false,
  traumatismos:false, cambiosAlimentacion:false, exposicionToxicos:false,
  contactoEnfermos:false, ultimaIngesta:'', ultimaAgua:'',
  temperatura:'', frecuenciaCardiaca:'', frecuenciaRespiratoria:'',
  presionSistolica:'', spO2:'', tlc:'', scoreDolor:'0', bcs:'5',
  mucosas:'rosadas', consciencia:'alerta', sistemasExamen:{},
  consentTratamiento:false, consentAnestesia:false, consentEmergencia:false,
  consentEutanasia:false, disposicionCuerpo:'retiro',
  estimadoMin:'', estimadoMax:'', nombreFirmante:'',
};

const AdmissionStepperDialog = ({ open, onClose, selectedHospital, selectedCage, patients, diagnoses, users, savingAdmission, admissionError, onSubmit, initialPatient = null, initialFormData = null, mode = 'admission' }) => {
  const [step, setStep] = React.useState(0);
  const [pat, setPat] = React.useState(initialPatient);
  const [form, setForm] = React.useState(INIT_FORM);
  const [localError, setLocalError] = React.useState('');
  React.useEffect(() => {
    if (open) {
      setStep(0);
      setLocalError('');
      setPat(initialPatient || null);
      setForm({
        ...INIT_FORM,
        ...(initialFormData || {}),
        patientId: initialPatient?.id || initialFormData?.patientId || '',
        pesoAlIngreso: initialFormData?.pesoAlIngreso || initialPatient?.weight || '',
        nombreFirmante: initialFormData?.nombreFirmante || initialPatient?.ownerName || '',
      });
    }
  }, [open, initialPatient, initialFormData]);

  const sf = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));
  const bf = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.checked }));
  const toggleSintoma = (s) => setForm(p => ({ ...p, sintomas: p.sintomas.includes(s) ? p.sintomas.filter(x=>x!==s) : [...p.sintomas, s] }));
  const setSistema = (name, val) => setForm(p => ({ ...p, sistemasExamen: { ...p.sistemasExamen, [name]: { ...(p.sistemasExamen[name]||{ normal:true, notas:'' }), ...val } } }));
  const onPatChange = (v) => { setPat(v); setForm(p => ({ ...p, patientId: v?.patientId||v?.id||'', pesoAlIngreso: v?.weight||'', nombreFirmante: v?.ownerName||'' })); };

  // ── Step 0: Paciente & Propietario ─────────────────────────────────────────
  const renderStep0 = () => (
    <Box>
      {!pat ? (
        <PatientSearch
          onPatientSelect={onPatChange}
          selectedPatient={pat}
          showFilters={false}
          label="Buscar paciente o propietario"
        />
      ) : (
        <Paper sx={{ p:1.5, bgcolor:'#F8FAFC', borderRadius:2, mb:1.5, border:'1px solid #E2E8F0' }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar src={pat.photoURL} sx={{ width:48, height:48, bgcolor:getSpeciesColor(pat.species) }}>
              <PetsIcon/>
            </Avatar>
            <Box sx={{ flex:1 }}>
              <Typography fontWeight={700}>{pat.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {pat.species} · {pat.breed} · {pat.genero} · {pat.weight} kg
              </Typography>
              {pat.microchip && <Typography variant="caption" color="text.secondary" display="block">Microchip: {pat.microchip}</Typography>}
            </Box>
            {!initialPatient && (
              <IconButton size="small" onClick={() => { setPat(null); setForm(p => ({ ...p, patientId: '', pesoAlIngreso: '', nombreFirmante: '' })); }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Paper>
      )}

      {pat && (
        <>
          {pat.alergias && <Alert severity="error" sx={{ mt:1, py:0.3, fontSize:'0.78rem' }}>⚠ ALERGIAS: {pat.alergias}</Alert>}
          {pat.condicionesCronicas && <Alert severity="warning" sx={{ mt:0.5, py:0.3, fontSize:'0.78rem' }}>{pat.condicionesCronicas}</Alert>}
          <Divider sx={{ my:1.5 }}/>
          <Typography variant="caption" fontWeight={700} color="text.secondary">PROPIETARIO</Typography>
          <Grid container spacing={1} sx={{ mt:0.5 }}>
            <Grid size={6}><Typography variant="body2">{pat.ownerName||'–'}</Typography><Typography variant="caption" color="text.secondary">Nombre</Typography></Grid>
            <Grid size={6}><Typography variant="body2">{pat.ownerCelular||pat.ownerPhone||'–'}</Typography><Typography variant="caption" color="text.secondary">Teléfono</Typography></Grid>
          </Grid>
        </>
      )}

      <Grid container spacing={2} sx={{ mt:1 }}>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <TextField size="small" fullWidth label="Peso al ingreso (kg)" type="number"
            value={form.pesoAlIngreso} onChange={sf('pesoAlIngreso')}
            InputProps={{ endAdornment:<InputAdornment position="end">kg</InputAdornment> }}/>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <TextField size="small" fullWidth select label="Tipo de ingreso" value={form.tipoIngreso} onChange={sf('tipoIngreso')}>
            <MenuItem value="urgencia">Urgencia</MenuItem>
            <MenuItem value="programado">Programado</MenuItem>
            <MenuItem value="referido">Referido</MenuItem>
            <MenuItem value="continuacion">Continuación de tratamiento</MenuItem>
          </TextField>
        </Grid>
        <Grid size={12}>
          <TextField size="small" fullWidth label="Motivo de ingreso (con palabras del propietario)"
            value={form.motivoIngreso} onChange={sf('motivoIngreso')} multiline rows={2}/>
        </Grid>
      </Grid>
    </Box>
  );

  // ── Step 1: Historia Clínica ────────────────────────────────────────────────
  const renderStep1 = () => (
    <Box>
      <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1}>CUADRO ACTUAL</Typography>
      <TextField size="small" fullWidth label="Tiempo de evolución de síntomas" value={form.tiempoEvolucion} onChange={sf('tiempoEvolucion')} sx={{ mb:1.5 }}/>

      <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={0.5}>Síntomas presentes:</Typography>
      <FormGroup row sx={{ mb:1.5, gap:0.5 }}>
        {SINTOMAS_OPC.map(s => (
          <FormControlLabel key={s} control={<Checkbox size="small" checked={form.sintomas.includes(s)} onChange={()=>toggleSintoma(s)}/>} label={<Typography variant="caption">{s}</Typography>}/>
        ))}
      </FormGroup>

      <FormControlLabel control={<Switch size="small" checked={form.tratamientoPrevio} onChange={bf('tratamientoPrevio')}/>}
        label="¿Ha recibido tratamiento previo para este cuadro?" sx={{ mb:1 }}/>
      {form.tratamientoPrevio && (
        <TextField size="small" fullWidth label="Medicamentos utilizados previamente" value={form.medicacionPrevia} onChange={sf('medicacionPrevia')} sx={{ mb:1.5 }}/>
      )}

      <Divider sx={{ my:2 }}/>
      <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1}>HISTORIA RELEVANTE</Typography>
      <Grid container spacing={1}>
        {[
          ['hospitalizacionesPrevias','¿Hospitalizaciones previas?'],
          ['cirugiasPrevias','¿Cirugías previas?'],
          ['traumatismos','¿Traumatismos recientes?'],
          ['cambiosAlimentacion','¿Cambios de alimentación recientes?'],
          ['exposicionToxicos','¿Exposición a tóxicos/venenos?'],
          ['contactoEnfermos','¿Contacto con animales enfermos?'],
        ].map(([field, label]) => (
          <Grid
            key={field}
            size={{
              xs: 12,
              sm: 6
            }}>
            <FormControlLabel control={<Switch size="small" checked={form[field]} onChange={bf(field)}/>} label={<Typography variant="caption">{label}</Typography>}/>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my:2 }}/>
      <Typography variant="caption" fontWeight={700} color={DANGER} display="block" mb={1}>⚠ CRÍTICO PARA ANESTESIA</Typography>
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <TextField size="small" fullWidth label="Última ingesta de alimento (fecha/hora)" value={form.ultimaIngesta} onChange={sf('ultimaIngesta')} type="datetime-local" InputLabelProps={{ shrink:true }}/>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <TextField size="small" fullWidth label="Última ingesta de agua (fecha/hora)" value={form.ultimaAgua} onChange={sf('ultimaAgua')} type="datetime-local" InputLabelProps={{ shrink:true }}/>
        </Grid>
      </Grid>
    </Box>
  );

  // ── Step 2: Examen Físico ───────────────────────────────────────────────────
  const renderStep2 = () => (
    <Box>
      <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1}>SIGNOS VITALES BASALES</Typography>
      <Grid container spacing={1.5} sx={{ mb:2 }}>
        {[
          ['temperatura','Temperatura','°C'],['frecuenciaCardiaca','Frec. Cardíaca','lpm'],
          ['frecuenciaRespiratoria','Frec. Respiratoria','rpm'],['presionSistolica','P.A. Sistólica','mmHg'],
          ['spO2','SpO2','%'],['tlc','TLC','seg'],['scoreDolor','Dolor (0-4)',''],
        ].map(([f,label,unit]) => (
          <Grid
            key={f}
            size={{
              xs: 6,
              sm: 4,
              md: 3
            }}>
            <TextField size="small" fullWidth label={label} value={form[f]} onChange={sf(f)} type="number"
              InputProps={unit ? { endAdornment:<InputAdornment position="end">{unit}</InputAdornment> } : {}}/>
          </Grid>
        ))}
        <Grid
          size={{
            xs: 6,
            sm: 4,
            md: 3
          }}>
          <TextField size="small" fullWidth label="BCS (1-9)" type="number" value={form.bcs} onChange={sf('bcs')} inputProps={{ min:1, max:9 }}/>
        </Grid>
        <Grid
          size={{
            xs: 6,
            sm: 4
          }}>
          <TextField size="small" fullWidth select label="Mucosas" value={form.mucosas} onChange={sf('mucosas')}>
            {['rosadas','pálidas','cianóticas','ictéricas','congestionadas','secas'].map(m=><MenuItem key={m} value={m}>{m.charAt(0).toUpperCase()+m.slice(1)}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid
          size={{
            xs: 6,
            sm: 4
          }}>
          <TextField size="small" fullWidth select label="Consciencia" value={form.consciencia} onChange={sf('consciencia')}>
            {['alerta','deprimido','estuporoso','comatoso'].map(m=><MenuItem key={m} value={m}>{m.charAt(0).toUpperCase()+m.slice(1)}</MenuItem>)}
          </TextField>
        </Grid>
      </Grid>

      <Divider sx={{ my:2 }}/>
      <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1}>EXAMEN POR SISTEMAS (14)</Typography>
      <Grid container spacing={1}>
        {SISTEMAS_OPC.map(sis => {
          const st = form.sistemasExamen[sis] || { normal:true, notas:'' };
          return (
            <Grid
              key={sis}
              size={{
                xs: 12,
                sm: 6
              }}>
              <Box sx={{ p:1, border:'1px solid #E2E8F0', borderRadius:2, bgcolor: st.normal ? 'white' : '#FFF5F5' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" fontWeight={600}>{sis}</Typography>
                  <Stack direction="row" spacing={0.5}>
                    <Chip label="Normal" size="small" onClick={()=>setSistema(sis,{normal:true})}
                      sx={{ cursor:'pointer', bgcolor: st.normal ? '#D1FAE5':'#F3F4F6', color: st.normal ? '#065F46':'#6B7280', fontWeight:700, fontSize:'0.6rem' }}/>
                    <Chip label="Anormal" size="small" onClick={()=>setSistema(sis,{normal:false})}
                      sx={{ cursor:'pointer', bgcolor: !st.normal ? '#FEE2E2':'#F3F4F6', color: !st.normal ? '#991B1B':'#6B7280', fontWeight:700, fontSize:'0.6rem' }}/>
                  </Stack>
                </Stack>
                {!st.normal && (
                  <TextField size="small" fullWidth placeholder="Describir hallazgos..." value={st.notas}
                    onChange={e=>setSistema(sis,{notas:e.target.value})} sx={{ mt:0.5 }}/>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );

  // ── Step 3: Plan & Consentimiento ──────────────────────────────────────────
  const renderStep3 = () => (
    <Box>
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <TextField size="small" fullWidth select label="Estado clínico al ingreso" value={form.clinicalStatus} onChange={sf('clinicalStatus')}>
            {Object.entries(CLINICAL_STATUS_CONFIG).map(([k,v])=><MenuItem key={k} value={k}>{v.label}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <Autocomplete size="small" options={users.filter(u=>['Médico Veterinario','Director Médico'].includes(u.role))}
            getOptionLabel={u=>`${u.name} ${u.lastName||''}`.trim()}
            value={users.find(u=>u.id===form.veterinarioId) || null}
            isOptionEqualToValue={(o,v)=>o.id===v.id}
            onChange={(_,v)=>setForm(p=>({...p,veterinarioId:v?.id||''}))}
            renderInput={p=><TextField {...p} label="Veterinario responsable"/>}/>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <Autocomplete size="small" options={diagnoses} getOptionLabel={o=>o.diagnosis||''}
            value={diagnoses.find(d=>d.id===form.diagnosisId) || null}
            isOptionEqualToValue={(o,v)=>o.id===v.id}
            onChange={(_,v)=>setForm(p=>({...p,diagnosisId:v?.id||'',primaryDiagnosis:v?.diagnosis||''}))}
            filterOptions={(opts,{inputValue})=>opts.filter(o=>o.diagnosis?.toLowerCase().includes(inputValue.toLowerCase()))}
            renderInput={p=><TextField {...p} label="Diagnóstico presuntivo principal"/>}/>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <TextField size="small" fullWidth label="Días estimados de internación" type="number" value={form.diasEstimados} onChange={sf('diasEstimados')}/>
        </Grid>
        <Grid size={12}><FormControlLabel control={<Switch checked={form.isInfectious} onChange={bf('isInfectious')}/>} label="¿Paciente infeccioso? (área de aislamiento)"/></Grid>
        <Grid size={12}><TextField size="small" fullWidth label="Notas adicionales de admisión" multiline rows={2} value={form.notes} onChange={sf('notes')}/></Grid>
      </Grid>

      <Divider sx={{ my:2 }}/>
      <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1}>CONSENTIMIENTO INFORMADO</Typography>
      <FormGroup>
        {[
          ['consentTratamiento','Autorizo hospitalización, medicamentos, exámenes y procedimientos necesarios.'],
          ['consentAnestesia','Entiendo los riesgos inherentes a la anestesia informados por el veterinario.'],
          ['consentEmergencia','En caso de emergencia sin contacto, el veterinario actuará según criterio clínico.'],
          ['consentEutanasia','Autorizo eutanasia humanitaria si el veterinario la estima médicamente necesaria y no es posible contactarme.'],
        ].map(([field,label])=>(
          <FormControlLabel key={field} control={<Checkbox checked={form[field]} onChange={bf(field)} size="small"/>}
            label={<Typography variant="caption">{label}</Typography>} sx={{ alignItems:'flex-start', mb:0.5 }}/>
        ))}
      </FormGroup>

      <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mt={2} mb={1}>DISPOSICIÓN DEL CUERPO EN CASO DE MUERTE</Typography>
      <RadioGroup row value={form.disposicionCuerpo} onChange={sf('disposicionCuerpo')}>
        <FormControlLabel value="retiro" control={<Radio size="small"/>} label={<Typography variant="caption">Retiro personal</Typography>}/>
        <FormControlLabel value="cremacion_individual" control={<Radio size="small"/>} label={<Typography variant="caption">Cremación individual</Typography>}/>
        <FormControlLabel value="cremacion_comunitaria" control={<Radio size="small"/>} label={<Typography variant="caption">Cremación comunitaria</Typography>}/>
      </RadioGroup>

      <Grid container spacing={2} sx={{ mt:1 }}>
        <Grid size={6}><TextField size="small" fullWidth label="Costo estimado mínimo" type="number" value={form.estimadoMin} onChange={sf('estimadoMin')} InputProps={{ startAdornment:<InputAdornment position="start">$</InputAdornment> }}/></Grid>
        <Grid size={6}><TextField size="small" fullWidth label="Costo estimado máximo" type="number" value={form.estimadoMax} onChange={sf('estimadoMax')} InputProps={{ startAdornment:<InputAdornment position="start">$</InputAdornment> }}/></Grid>
        <Grid size={12}><TextField size="small" fullWidth label="Nombre del firmante (propietario o representante)" value={form.nombreFirmante} onChange={sf('nombreFirmante')}/></Grid>
      </Grid>

      <Box sx={{ p:1.5, bgcolor:'#F0F9FF', borderRadius:2, border:`1px solid #BAE6FD`, mt:2 }}>
        <Typography variant="caption" fontWeight={700} color={INFO}>
          📍 {mode === 'order' ? 'Jaula por asignar en hospital' : `Ingresando a: ${selectedHospital?.location} — ${selectedCage}`}
        </Typography>
        {form.isInfectious && <Alert severity="warning" sx={{ mt:0.5, py:0.3, fontSize:'0.75rem' }}>Área de aislamiento infeccioso</Alert>}
      </Box>
    </Box>
  );

  const stepContent = [renderStep0(), renderStep1(), renderStep2(), renderStep3()];

  const handleFinalSubmit = () => {
    if (!form.veterinarioId) { setLocalError('Selecciona el veterinario responsable.'); return; }
    if (!form.consentTratamiento) { setLocalError('Debes aceptar el consentimiento de hospitalización y tratamiento.'); return; }
    if (!(form.nombreFirmante || '').trim()) { setLocalError('Ingresa el nombre de quien firma el consentimiento.'); return; }
    setLocalError('');
    onSubmit(form, pat);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx:{ maxHeight:'90vh' } }}>
      <DialogTitle sx={{ color:'#FFFFFF', fontWeight:700, pb:0, bgcolor:PRIMARY, px:3, pt:2 }}>
        {mode === 'order' ? '🏥 Orden de Hospitalización' : mode === 'review' ? '🏥 Revisar Ingreso Hospitalario' : '🏥 Hoja de Ingreso Hospitalario'}
        <Typography variant="caption" color="rgba(255,255,255,0.85)" display="block">
          {mode === 'order' ? 'Paciente a hospitalizar — jaula por asignar' : `${selectedHospital?.location} — ${selectedCage}`}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt:2 }}>
        <Stepper activeStep={step} sx={{ mb:3 }}>
          {ADM_STEPS.map(label=><Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>
        {(admissionError || localError) && <Alert severity="error" sx={{ mb:2 }}>{admissionError || localError}</Alert>}
        {stepContent[step]}
      </DialogContent>
      <DialogActions sx={{ px:3, pb:2 }}>
        <Button onClick={onClose} disabled={savingAdmission}>Cancelar</Button>
        <Box sx={{ flex:1}}/>
        {step > 0 && <Button onClick={()=>setStep(s=>s-1)}>Anterior</Button>}
        {step < 3 ? (
          <Button variant="contained" onClick={()=>setStep(s=>s+1)} disabled={step===0 && (!form.patientId || !(form.motivoIngreso||'').trim())} sx={{ bgcolor:PRIMARY }}>
            Siguiente →
          </Button>
        ) : (
          <Button variant="contained" onClick={handleFinalSubmit} disabled={savingAdmission || !form.patientId} sx={{ bgcolor:PRIMARY }}>
            {savingAdmission ? 'Guardando…' : mode === 'order' ? '✓ Crear orden de hospitalización' : '✓ Confirmar Ingreso'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ─── PendingOrdersDialog ──────────────────────────────────────────────────────
const checkPresupuestoAdmission = (presupuesto) => {
  if (!presupuesto) return { ready: false, reason: 'Sin presupuesto', pct: 0 };
  const total = Number(presupuesto.total) || 0;
  const pagado = Number(presupuesto.montoPagado) || 0;
  const pct = total > 0 ? Math.round((pagado / total) * 100) : 0;
  if (presupuesto.estado === 'pagado') return { ready: true, reason: 'Pagado', pct: 100 };
  if (pct >= 70) return { ready: true, reason: `Abono ${pct}%`, pct };
  return { ready: false, reason: `Abono ${pct}% (mín 70%)`, pct };
};

const PendingOrdersDialog = ({ open, onClose, pendingOrders, onSelect, selectedCage, selectedHospital, presupuestosById }) => {
  const [filter, setFilter] = useState('');
  const filtered = pendingOrders.filter(o => {
    if (!filter) return true;
    const term = filter.toLowerCase();
    return (o.patientName || '').toLowerCase().includes(term) ||
           (o.ownerName || '').toLowerCase().includes(term) ||
           (o.primaryDiagnosis || '').toLowerCase().includes(term);
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: PRIMARY, fontWeight: 700 }}>
        Asignar paciente a {selectedHospital?.location || ''} — {selectedCage}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Selecciona una orden de hospitalización pendiente para asignar a esta jaula.
          Sólo aparecen habilitadas las órdenes con presupuesto pagado o abono ≥ 70%.
        </Typography>
        <TextField size="small" fullWidth placeholder="Buscar paciente o propietario..."
          value={filter} onChange={e => setFilter(e.target.value)} sx={{ mb: 2 }}
          InputProps={{ startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 0.5, fontSize: 18 }}/> }}/>
        {filtered.length === 0 ? (
          <Paper elevation={0} sx={{ p: 3, textAlign: 'center', borderRadius: 2, border: '1px solid #E2E8F0' }}>
            <Typography variant="body2" color="text.secondary">No hay órdenes de hospitalización pendientes.</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Crea una orden desde la ficha del paciente (sección Hospital).
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={1}>
            {filtered.map(o => {
              const pres = o.presupuestoId && presupuestosById ? presupuestosById[o.presupuestoId] : null;
              const check = checkPresupuestoAdmission(pres);
              return (
                <Paper key={o.id} elevation={0} sx={{
                  p: 1.5, borderRadius: 2, border: '1px solid #E2E8F0',
                  cursor: check.ready ? 'pointer' : 'not-allowed',
                  opacity: check.ready ? 1 : 0.6,
                  '&:hover': check.ready ? { borderColor: TEAL, bgcolor: '#F0FDFA' } : {},
                }}
                  onClick={() => check.ready && onSelect(o)}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ width: 38, height: 38, bgcolor: getSpeciesColor(o.patientSpecies) }}>
                      <PetsIcon sx={{ fontSize: 18 }}/>
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700} noWrap>{o.patientName || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {o.patientSpecies} · {o.patientBreed || '–'} · {o.patientWeight || '?'} kg
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        Propietario: {o.ownerName || '—'}
                      </Typography>
                      {pres && (
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                          <Chip label={check.reason} size="small"
                            sx={{
                              bgcolor: check.ready ? '#D1FAE5' : '#FEF3C7',
                              color: check.ready ? '#065F46' : '#92400E',
                              fontSize: '0.6rem', fontWeight: 700, height: 18,
                            }} />
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                            {pres.numero || ''} · {CLP(pres.total)}
                          </Typography>
                        </Stack>
                      )}
                      {!pres && (
                        <Typography variant="caption" sx={{ color: DANGER, fontSize: '0.65rem', display: 'block', mt: 0.5 }}>
                          ⚠ Sin presupuesto asignado
                        </Typography>
                      )}
                    </Box>
                    <Button size="small" variant="outlined"
                      disabled={!check.ready}
                      sx={{ color: check.ready ? TEAL : '#94A3B8', borderColor: check.ready ? TEAL : '#CBD5E1', fontSize: '0.7rem', flexShrink: 0 }}>
                      {check.ready ? 'Seleccionar' : 'Bloqueado'}
                    </Button>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── HospitalListView ─────────────────────────────────────────────────────────
const HospitalListView = ({ hospitalizations, patients, onOpenFlowsheet, onOpenRecord, onDischarge, now }) => {
  const rows = hospitalizations.map(h => ({
    ...h, patient: patients.find(p => p.id === h.patientId),
    dias: calcDiasInternado(h.admissionAt),
    ttoSt: calcTreatmentStatus(h.nextTreatmentAt, now, h.nextTreatmentName || ''),
  })).sort((a,b) => {
    const order = { critico:0, alerta:1, estable:2, recuperacion:3 };
    return (order[a.clinicalStatus]??3) - (order[b.clinicalStatus]??3);
  });

  return (
    <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 3, overflowX: 'auto' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow sx={{ bgcolor: '#F8FAFC' }}>
            {['Jaula','Paciente','Especie/Raza','Estado','Diagnóstico','Días','Próx. Tto','Acciones'].map(h => (
              <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', bgcolor: '#F8FAFC' }}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(r => {
            const st = CLINICAL_STATUS_CONFIG[r.clinicalStatus] || CLINICAL_STATUS_CONFIG.estable;
            return (
              <TableRow key={r.id} sx={{ bgcolor: r.clinicalStatus === 'critico' ? '#FFF5F5' : 'white', '&:hover': { bgcolor: '#F8FAFC' } }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>{r.cageName}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{r.patient?.name || r.patientName}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">{r.ownerName || '–'}</Typography>
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>{r.patientSpecies} · {r.patientBreed || '–'}</TableCell>
                <TableCell><Chip label={st.label} size="small" sx={{ bgcolor: st.bg, color: st.color, fontSize: '0.65rem', fontWeight: 700 }}/></TableCell>
                <TableCell sx={{ fontSize: '0.72rem', maxWidth: 140 }}>
                  <Typography noWrap sx={{ fontSize: '0.72rem' }}>{r.primaryDiagnosis || '–'}</Typography>
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>{r.dias}</TableCell>
                <TableCell>
                  <Chip icon={r.ttoSt.isOverdue ? <WarningIcon sx={{ fontSize: '0.7rem!important', color: r.ttoSt.chipTextColor + '!important' }}/> : null}
                    label={r.ttoSt.label} size="small"
                    sx={{ bgcolor: r.ttoSt.chipColor, color: r.ttoSt.chipTextColor, fontSize: '0.65rem', fontWeight: 700, height: 'auto', minHeight: 20, border: r.ttoSt.isOverdue ? `1px solid ${r.ttoSt.chipTextColor}` : 'none' }}/>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Hoja de tratamiento"><IconButton size="small" onClick={() => onOpenFlowsheet(r.id)} sx={{ color: PRIMARY }}>
                      <MedicalServicesIcon sx={{ fontSize: 16 }}/>
                    </IconButton></Tooltip>
                    <Tooltip title="Ver ficha"><IconButton size="small" onClick={() => onOpenRecord(r.patientId)} sx={{ color: PRIMARY }}>
                      <AssignmentIcon sx={{ fontSize: 16 }}/>
                    </IconButton></Tooltip>
                    <Tooltip title="Dar de alta"><IconButton size="small" onClick={() => onDischarge(r)} sx={{ color: DANGER }}>
                      <LocalHospitalIcon sx={{ fontSize: 16 }}/>
                    </IconButton></Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>Sin pacientes hospitalizados</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
};

const Hospital = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlPatientId = searchParams.get('patientId');
  const isNewOrder = searchParams.get('newOrder') === 'true';

  // Static data (loaded once)
  const [capacities, setCapacities]   = useState([]);
  const [patients,   setPatients]     = useState([]);
  const [owners,     setOwners]       = useState([]);
  const [diagnoses,  setDiagnoses]    = useState([]);
  const [users,      setUsers]        = useState([]);
  const [loading,    setLoading]      = useState(true);
  const [error,      setError]        = useState('');
  const { activeBranchId, isAllBranches, filterByBranch } = useBranch();
  const { currentUser, userData } = useAuth();

  // Real-time hospitalizations via onSnapshot
  const [hospitalizations, setHospitalizations] = useState([]);
  const [cleaningPending,   setCleaningPending]   = useState([]);
  const [pendingOrders,    setPendingOrders]    = useState([]);
  const [presupuestos,     setPresupuestos]     = useState([]);
  const [lastUpdated,      setLastUpdated]       = useState(null);
  const [now, setNow] = useState(new Date());

  // UI state
  const [tabValue,    setTabValue]    = useState(0);
  const [viewMode,    setViewMode]    = useState('grid'); // 'grid' | 'list'
  const [tvMode,      setTvMode]      = useState(false);
  const [searchTerm,  setSearchTerm]  = useState('');
  const [filterStatus,setFilterStatus]= useState('todos');
  const [sortBy,      setSortBy]      = useState('estado');
  const [snackbar,    setSnackbar]    = useState({ open: false, message: '', severity: 'success' });

  // Admission dialog
  const [admissionDialogOpen, setAdmissionDialogOpen] = useState(false);
  const [selectedHospital,    setSelectedHospital]     = useState(null);
  const [selectedCage,        setSelectedCage]         = useState('');
  const [savingAdmission,     setSavingAdmission]      = useState(false);
  const [admissionError,      setAdmissionError]       = useState('');
  const [newOrderMode,        setNewOrderMode]        = useState(false);

  // Pending orders dialog
  const [pendingOrdersOpen, setPendingOrdersOpen] = useState(false);
  const [selectedOrder,     setSelectedOrder]     = useState(null);

  // Discharge dialog
  const [dischargeDialogOpen, setDischargeDialogOpen] = useState(false);
  const [selectedDischarge,   setSelectedDischarge]   = useState(null);
  const [savingDischarge,     setSavingDischarge]      = useState(false);

  // ── Real-time hospitalizations ───────────────────────────────────────────────
  useEffect(() => {
    const q = query(collection(db, 'hospitalizations'), where('status', '!=', 'alta'));
    const unsub = onSnapshot(q, (snap) => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setHospitalizations(filterByBranch(all, { includeUnassigned: true }));
      setLastUpdated(new Date());
    }, (err) => console.error('onSnapshot error:', err));
    return () => unsub();
  }, [filterByBranch]);

  // ── Real-time cleaning-pending hospitalizations (status=alta & limpiezaPendiente=true) ──
  useEffect(() => {
    const q = query(collection(db, 'hospitalizations'), where('limpiezaPendiente', '==', true));
    const unsub = onSnapshot(q, (snap) => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setCleaningPending(filterByBranch(all, { includeUnassigned: true }));
    }, (err) => console.error('onSnapshot cleaning error:', err));
    return () => unsub();
  }, [filterByBranch]);

  // ── Real-time pending hospitalization orders ─────────────────────────────────
  useEffect(() => {
    const q = query(collection(db, 'hospitalizationOrders'), where('status', '==', 'pendiente'));
    const unsub = onSnapshot(q, (snap) => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setPendingOrders(filterByBranch(all, { includeUnassigned: true }));
    }, (err) => console.error('onSnapshot pending orders error:', err));
    return () => unsub();
  }, [filterByBranch]);

  // ── Static data load once ────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [caps, pats, ownrs, diags, usrs, press] = await Promise.all([
          getCollection('capacities'),
          getCollection('patients'),
          getCollection('owners'),
          getCollection('diagnoses'),
          getCollection('users'),
          getCollection('presupuestos'),
        ]);
        setCapacities(filterByBranch(caps.filter(c => c.type === 'hospital' || c.type === 'hospitalization'), { includeUnassigned: true }));
        setPatients(pats);
        setOwners(ownrs);
        setDiagnoses(diags);
        setUsers(usrs);
        setPresupuestos(press || []);
      } catch (e) {
        setError('Error al cargar datos');
      } finally {
        setLoading(false);
      }
    })();
  }, [filterByBranch]);

  // ── Auto-open new order admission dialog from PatientRecord ────────────────────
  useEffect(() => {
    if (isNewOrder && urlPatientId && patients.length > 0 && !admissionDialogOpen) {
      const p = patients.find(x => x.id === urlPatientId);
      if (p) {
        setNewOrderMode(true);
        setSelectedHospital(null);
        setSelectedCage('');
        setAdmissionError('');
        setAdmissionDialogOpen(true);
      }
    }
  }, [isNewOrder, urlPatientId, patients, admissionDialogOpen]);

  // ── Update 'now' every 60s for treatment status recalc ───────────────────────
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  // ── Update lastUpdated display every 30s ─────────────────────────────────────
  const [, forceRender] = useState(0);
  useEffect(() => {
    const id = setInterval(() => forceRender(n => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const enrichPatient = useCallback((p) => {
    if (!p) return null;
    const o = owners.find((x) => x.id === p.ownerId) || {};
    return {
      ...p,
      ownerName: p.ownerName || o.name || '',
      ownerCelular: p.ownerCelular || o.celular || o.phone || '',
      ownerPhone: p.ownerPhone || o.celular || o.phone || '',
      ownerConvenio: p.ownerConvenio || o.convenio || '',
    };
  }, [owners]);

  const dialogInitialPatient = useMemo(() => {
    const raw = newOrderMode
      ? patients.find((p) => p.id === urlPatientId)
      : selectedOrder
        ? patients.find((p) => p.id === selectedOrder.patientId)
        : null;
    return enrichPatient(raw);
  }, [newOrderMode, selectedOrder, patients, urlPatientId, enrichPatient]);

  const buildHospitalizationData = (form, selectedPatient, cageCap, cageName, now = new Date()) => {
    const fechaAltaEstimada = form.diasEstimados
      ? new Date(now.getTime() + parseInt(form.diasEstimados) * 86400000)
      : null;
    const vet = users.find(u => u.id === form.veterinarioId);
    return {
      hospitalCapacityId: cageCap?.id || '',
      hospitalLocation: cageCap?.location || '',
      cageName: cageName || '',
      branchId: activeBranchId || '',
      status: 'ingresado',
      clinicalStatus: form.clinicalStatus || 'estable',
      patientId: selectedPatient.id,
      ownerId: selectedPatient.ownerId || null,
      patientName: selectedPatient.name || '',
      patientSpecies: selectedPatient.species || '',
      patientBreed: selectedPatient.breed || '',
      patientWeight: form.pesoAlIngreso || selectedPatient.weight || '',
      patientAlergias: selectedPatient.alergias || '',
      ownerName: selectedPatient.ownerName || '',
      ownerPhone: selectedPatient.ownerCelular || selectedPatient.ownerPhone || '',
      ownerConvenio: selectedPatient.ownerConvenio || '',
      primaryDiagnosis: form.primaryDiagnosis || '',
      motivoIngreso: form.motivoIngreso || '',
      tipoIngreso: form.tipoIngreso || 'urgencia',
      isInfectious: form.isInfectious || false,
      diasEstimadosInternacion: parseInt(form.diasEstimados) || null,
      fechaAltaEstimada,
      veterinarioResponsableId: form.veterinarioId || '',
      veterinarioResponsableNombre: vet ? `${vet.name} ${vet.lastName||''}`.trim() : '',
      notes: form.notes || '',
      admissionAt: now, createdAt: now, updatedAt: now,
      hojaIngreso: {
        completadaAt: now,
        completadaPor: form.veterinarioId || '',
        anamnesis: {
          tiempoEvolucion: form.tiempoEvolucion,
          sintomas: form.sintomas,
          tratamientoPrevio: form.tratamientoPrevio,
          medicacionPrevia: form.medicacionPrevia,
          hospitalizacionesPrevias: form.hospitalizacionesPrevias,
          cirugiasPrevias: form.cirugiasPrevias,
          traumatismos: form.traumatismos,
          cambiosAlimentacion: form.cambiosAlimentacion,
          exposicionToxicos: form.exposicionToxicos,
          contactoEnfermos: form.contactoEnfermos,
          ultimaIngesta: form.ultimaIngesta,
          ultimaAgua: form.ultimaAgua,
        },
        examenFisicoIngreso: {
          temperatura: form.temperatura, frecuenciaCardiaca: form.frecuenciaCardiaca,
          frecuenciaRespiratoria: form.frecuenciaRespiratoria, presionSistolica: form.presionSistolica,
          spO2: form.spO2, tlc: form.tlc, scoreDolor: form.scoreDolor,
          bcs: form.bcs, mucosas: form.mucosas, consciencia: form.consciencia,
          sistemasExamen: form.sistemasExamen,
        },
        consentimiento: {
          clausulas: { tratamiento: form.consentTratamiento, anestesia: form.consentAnestesia, emergencia: form.consentEmergencia, eutanasia: form.consentEutanasia },
          disposicionCuerpo: form.disposicionCuerpo,
          estimadoCostoMin: form.estimadoMin, estimadoCostoMax: form.estimadoMax,
          nombreFirmante: form.nombreFirmante, firmadoAt: now,
        },
      },
    };
  };

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleOpenAdmission = useCallback((hospital, cageName) => {
    setSelectedHospital(hospital);
    setSelectedCage(cageName);
    setAdmissionError('');
    setPendingOrdersOpen(true);
  }, []);

  const handleSelectPendingOrder = useCallback((order) => {
    setSelectedOrder(order);
    setPendingOrdersOpen(false);
    setNewOrderMode(false);
    setAdmissionError('');
    setAdmissionDialogOpen(true);
  }, []);

  const handleCloseAdmission = useCallback(() => {
    setAdmissionDialogOpen(false);
    setSelectedHospital(null);
    setSelectedCage('');
    setSelectedOrder(null);
    setNewOrderMode(false);
    if (isNewOrder && urlPatientId) navigate(`/patients/${urlPatientId}/record`, { replace: true });
  }, [isNewOrder, urlPatientId, navigate]);

  // Presupuesto automático de la orden de hospitalización: día cama × días estimados
  const crearPresupuestoOrdenHospitalizacion = useCallback(async (orderId, form, selectedPatient) => {
    const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const aSnap = await getDocs(collection(db, 'aranceles'));
    const activos = filterByBranch(aSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((a) => a.activo !== false), { includeUnassigned: true });
    const deHospital = activos.filter((a) => norm(a.tipoPrestacionName || a.tipoPrestacion).includes('hospitaliza'));
    const arancel = deHospital.find((a) => norm(a.prestacion).includes('dia cama')) || deHospital[0] || null;
    if (!arancel) {
      console.warn('No se encontró arancel de hospitalización (día cama); no se generó presupuesto.');
      return null;
    }
    const dias = Math.max(1, parseInt(form.diasEstimados) || 1);
    const vetResponsible = users.find(u => u.id === form.veterinarioId);
    return crearPresupuestoAutomatico({
      orderId,
      orderType: 'hospitalizationOrder',
      orderNumber: '',
      patientId: selectedPatient.id,
      patientName: selectedPatient.name || '',
      patientSpecies: selectedPatient.species || '',
      ownerId: selectedPatient.ownerId || '',
      ownerName: selectedPatient.ownerName || '',
      ownerPhone: selectedPatient.ownerCelular || selectedPatient.ownerPhone || '',
      convenioName: selectedPatient.ownerConvenio || '',
      branchId: activeBranchId || '',
      vetId: form.veterinarioId || currentUser?.uid || '',
      vetName: vetResponsible ? `${vetResponsible.name} ${vetResponsible.lastName||''}`.trim() : (userData?.name || currentUser?.email || ''),
      items: [{
        arancel,
        cantidad: dias,
        tipoPrestacionFallback: 'Hospitalización',
        notas: `Día cama × ${dias} día(s) estimados de internación`,
      }],
      notas: 'Generado automáticamente desde orden de hospitalización',
      updateOrderCollection: 'hospitalizationOrders',
    });
  }, [activeBranchId, currentUser, userData]);

  const handleSubmitAdmission = useCallback(async (form, selectedPatient) => {
    if (!selectedPatient) { setAdmissionError('Debes seleccionar un paciente.'); return; }
    if (newOrderMode) {
      if (pendingOrders.some((o) => o.patientId === selectedPatient.id)) {
        setAdmissionError('Este paciente ya tiene una orden de hospitalización pendiente.'); return;
      }
      if (hospitalizations.some((h) => h.patientId === selectedPatient.id)) {
        setAdmissionError('Este paciente ya se encuentra hospitalizado.'); return;
      }
    }
    setSavingAdmission(true);
    setAdmissionError('');
    try {
      const n = new Date();
      if (newOrderMode) {
        // Create pending hospitalization order from PatientRecord
        const orderId = await addDocument('hospitalizationOrders', {
          status: 'pendiente',
          patientId: selectedPatient.id,
          ownerId: selectedPatient.ownerId || null,
          patientName: selectedPatient.name || '',
          patientSpecies: selectedPatient.species || '',
          patientBreed: selectedPatient.breed || '',
          patientWeight: form.pesoAlIngreso || selectedPatient.weight || '',
          patientAlergias: selectedPatient.alergias || '',
          ownerName: selectedPatient.ownerName || '',
          branchId: activeBranchId || '',
          formData: { ...form, patientId: selectedPatient.id },
          createdBy: currentUser?.uid || '',
          createdByName: userData?.name || currentUser?.email || '',
          createdAt: n, updatedAt: n,
        });
        try {
          await crearPresupuestoOrdenHospitalizacion(orderId, form, selectedPatient);
        } catch (err) {
          console.error('Error creando presupuesto de hospitalización:', err);
        }
        setAdmissionDialogOpen(false);
        setNewOrderMode(false);
        setSnackbar({ open: true, message: `Orden de hospitalización creada para ${selectedPatient.name}`, severity: 'success' });
        if (isNewOrder && urlPatientId) navigate('/hospital', { replace: true });
      } else {
        // Confirm admission from pending order (cage assignment)
        const hospData = buildHospitalizationData(form, selectedPatient, selectedHospital, selectedCage, n);
        const hospId = await addDocument('hospitalizations', hospData);
        if (selectedOrder?.id) {
          await updateDocument('hospitalizationOrders', selectedOrder.id, { status: 'ingresado', hospitalizationId: hospId, updatedAt: n });
        }
        try {
          await updateDocument('patients', selectedPatient.id, { status: 'hospitalizado', updatedAt: n });
        } catch (err) { console.error('No se pudo actualizar estado del paciente:', err); }
        setAdmissionDialogOpen(false);
        setSelectedHospital(null);
        setSelectedCage('');
        setSelectedOrder(null);
        setSnackbar({ open: true, message: `${selectedPatient.name} ingresado correctamente en ${selectedCage}`, severity: 'success' });
      }
    } catch (e) {
      console.error(e);
      setAdmissionError('Error al procesar la hospitalización. Intenta nuevamente.');
    } finally {
      setSavingAdmission(false);
    }
  }, [newOrderMode, selectedHospital, selectedCage, selectedOrder, isNewOrder, urlPatientId, navigate, users, pendingOrders, hospitalizations, activeBranchId, currentUser, userData, crearPresupuestoOrdenHospitalizacion]);

  const handleDischarge = useCallback((hosp) => {
    setSelectedDischarge(hosp);
    setDischargeDialogOpen(true);
  }, []);

  const handleConfirmDischarge = useCallback(async (form) => {
    setSavingDischarge(true);
    try {
      const altaAt = new Date();
      await updateDocument('hospitalizations', selectedDischarge.id, {
        status: 'alta', altaAt,
        altaDiagnosticoEgreso: form.diagnosticoEgreso,
        altaNotasEgreso: form.notasAlta || '',
        checklistAlta: form.checks || {},
        checklistAltaNotas: form.notas || {},
        limpiezaPendiente: true, updatedAt: altaAt,
      });
      if (selectedDischarge.patientId) {
        try {
          await updateDocument('patients', selectedDischarge.patientId, { status: 'activo', updatedAt: altaAt });
        } catch (err) { console.error('No se pudo actualizar estado del paciente:', err); }
      }
      await syncEgresoHospitalario({
        hospitalization: { id: selectedDischarge.id, ...selectedDischarge },
        patient: patientsMap[selectedDischarge.patientId],
        epi: {
          diagnosticoDefinitivo: form.diagnosticoEgreso,
          condicionAlta: form.condicionAlta || '',
          complicaciones: false,
        },
        diagnoses,
        altaAt,
      }).catch(err => console.error('Error sincronizando egreso:', err));
      setDischargeDialogOpen(false);
      setSnackbar({ open: true, message: `${selectedDischarge.patientName} dado de alta. Jaula ${selectedDischarge.cageName} pendiente de limpieza.`, severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Error al dar de alta', severity: 'error' });
    } finally {
      setSavingDischarge(false);
    }
  }, [selectedDischarge]);

  const handleMarkCleaned = useCallback(async (hosp) => {
    // Optimistic update: remove from local state immediately
    setCleaningPending(prev => prev.filter(h => h.id !== hosp.id));
    try {
      await updateDocument('hospitalizations', hosp.id, { limpiezaPendiente: false, updatedAt: new Date() });
      setSnackbar({ open: true, message: `Jaula ${hosp.cageName} marcada como limpia. Disponible para nuevos ingresos.`, severity: 'success' });
    } catch {
      // Revert on error: re-add the document to cleaningPending
      setCleaningPending(prev => [...prev, hosp]);
      setSnackbar({ open: true, message: 'Error al marcar como limpia', severity: 'error' });
    }
  }, []);

  const toggleTvMode = useCallback(() => {
    if (!tvMode) document.documentElement.requestFullscreen?.();
    else document.exitFullscreen?.();
    setTvMode(v => !v);
  }, [tvMode]);

  // ── Derived data ──────────────────────────────────────────────────────────────
  const patientsMap = useMemo(() => Object.fromEntries(patients.map(p => [p.id, p])), [patients]);
  const ownersMap   = useMemo(() => Object.fromEntries(owners.map(o => [o.id, o])), [owners]);
  const presupuestosById = useMemo(() => Object.fromEntries(presupuestos.map(p => [p.id, p])), [presupuestos]);

  const hospitalizationsWithConvenio = useMemo(() => {
    return hospitalizations.map(h => {
      const p = patientsMap[h.patientId];
      const o = ownersMap[h.ownerId] || (p && ownersMap[p.ownerId]) || {};
      return { ...h, ownerConvenio: h.ownerConvenio || o.convenio || '' };
    });
  }, [hospitalizations, patientsMap, ownersMap]);

  const branchHospitalizations = useMemo(() =>
    // Contar solo internaciones cuya jaula/capacidad pertenece a la sucursal activa
    hospitalizationsWithConvenio.filter(h => capacities.some(cap => cap.id === h.hospitalCapacityId)),
    [hospitalizationsWithConvenio, capacities]
  );

  const stats = useMemo(() => {
    const totalCages = capacities.reduce((s,c) => s + (c.resources?.length||0), 0);
    const criticos = branchHospitalizations.filter(h => h.clinicalStatus === 'critico').length;
    const overdue  = branchHospitalizations.filter(h => {
      if (!h.nextTreatmentAt) return false;
      return (toDate(h.nextTreatmentAt) < now);
    }).length;
    const today = new Date(); today.setHours(0,0,0,0);
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate()+1);
    const altaHoy = branchHospitalizations.filter(h => {
      if (!h.fechaAltaEstimada) return false;
      const f = toDate(h.fechaAltaEstimada);
      return f >= today && f < tomorrow;
    }).length;
    return {
      totalInternados: branchHospitalizations.length, totalCages,
      ocupacionPct: totalCages > 0 ? Math.round((branchHospitalizations.length/totalCages)*100) : 0,
      criticos, overdue, altaHoy,
      limpiezaPendiente: cleaningPending.length,
    };
  }, [hospitalizations, capacities, now, cleaningPending]);

  const speciesTabs = useMemo(() => {
    const sp = new Set(['todos']);
    capacities.forEach(c => {
      const loc = (c.location||'').toLowerCase();
      if (loc.includes('canino')||loc.includes('perro')) sp.add('canino');
      else if (loc.includes('felino')||loc.includes('gato')) sp.add('felino');
      else if (c.location) sp.add('otros');
    });
    return Array.from(sp);
  }, [capacities]);

  const activeSpecies = speciesTabs[tabValue] || 'todos';

  const buildGroup = useCallback((isInfectiousGroup) => {
    const allHosps = hospitalizationsWithConvenio;
    const speciesCapacities = capacities.filter(cap => {
      const loc = (cap.location||'').toLowerCase();
      if (activeSpecies === 'todos') return true;
      if (activeSpecies === 'canino') return loc.includes('canino')||loc.includes('perro');
      if (activeSpecies === 'felino') return loc.includes('felino')||loc.includes('gato');
      return !loc.includes('canino')&&!loc.includes('felino');
    });

    const groupCaps = speciesCapacities.filter(cap => {
      const isCapInfectious = (cap.location||'').toLowerCase().includes('infecc');
      return isCapInfectious === isInfectiousGroup;
    });
    if (groupCaps.length === 0) return null;

    const resourcesEntries = groupCaps.flatMap(cap =>
      (cap.resources||[]).map(rn => ({ cap, resourceName: rn }))
    );

    const groupHosps = allHosps.filter(h => {
      const belongsToGroup = groupCaps.some(cap => cap.id === h.hospitalCapacityId);
      return belongsToGroup;
    });

    // Apply search + filter
    const filteredHosps = groupHosps.filter(h => {
      if (filterStatus !== 'todos' && h.clinicalStatus !== filterStatus) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return (
          h.patientName?.toLowerCase().includes(term) ||
          h.ownerName?.toLowerCase().includes(term) ||
          h.primaryDiagnosis?.toLowerCase().includes(term) ||
          h.cageName?.toLowerCase().includes(term)
        );
      }
      return true;
    }).sort((a,b) => {
      if (sortBy === 'estado') {
        const order = { critico:0, alerta:1, estable:2, recuperacion:3 };
        return (order[a.clinicalStatus]??3)-(order[b.clinicalStatus]??3);
      }
      if (sortBy === 'proximoTto') {
        return (toDate(a.nextTreatmentAt)||new Date(0)) - (toDate(b.nextTreatmentAt)||new Date(0));
      }
      if (sortBy === 'diasInternado') {
        return (toDate(a.admissionAt)||new Date(0)) - (toDate(b.admissionAt)||new Date(0));
      }
      return (a.cageName||'').localeCompare(b.cageName||'', 'es');
    });

    const occupiedCageNames = new Set(groupHosps.map(h => h.cageName).filter(Boolean));
    const totalResources = resourcesEntries.length;
    const occupiedCount = occupiedCageNames.size;
    const occupancyPct = totalResources ? Math.round((occupiedCount/totalResources)*100) : 0;

    const speciesLabel = activeSpecies === 'todos' ? 'Todos' : activeSpecies.charAt(0).toUpperCase()+activeSpecies.slice(1);
    const title = `${speciesLabel}${isInfectiousGroup ? ' — Área Infecciosa' : ''}`;

    return (
      <Box key={title} mb={3}>
        {/* Section header */}
        <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:1.5, px:0.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1" fontWeight={700} color={PRIMARY}>{title}</Typography>
            {isInfectiousGroup && <Chip label="Aislamiento" size="small" color="error" sx={{ fontSize:'0.65rem' }}/>}
          </Stack>
          <Box sx={{ textAlign:'right', minWidth:160 }}>
            <Typography variant="caption" color="text.secondary">Ocupación: {occupiedCount}/{totalResources}</Typography>
            <LinearProgress variant="determinate" value={occupancyPct}
              sx={{ mt:0.5, height:6, borderRadius:3, bgcolor:'#E2E8F0',
                '& .MuiLinearProgress-bar':{ bgcolor: getOccupancyColor(occupancyPct) }}}/>
          </Box>
        </Box>
        <Grid container spacing={tvMode ? 2 : 1.5}>
          {resourcesEntries.map(({ cap, resourceName }, idx) => {
            const displayHosp = groupHosps.find(h => h.hospitalCapacityId===cap.id && h.cageName===resourceName) || null;
            const patient = displayHosp ? patients.find(p => p.id === displayHosp.patientId) : null;
            const cleaningHosp = !displayHosp ? cleaningPending.find(h => h.hospitalCapacityId===cap.id && h.cageName===resourceName) || null : null;
            return (
              <Grid
                key={`${cap.id}-${resourceName}-${idx}`}
                size={{
                  xs: tvMode ? 4 : 6,
                  sm: tvMode ? 3 : 4,
                  md: tvMode ? 3 : 4,
                  lg: tvMode ? 2 : 3
                }}>
                <CageCard
                  resourceName={resourceName}
                  hospitalization={displayHosp}
                  patient={patient}
                  onAdmit={() => handleOpenAdmission(cap, resourceName)}
                  onOpenFlowsheet={id => navigate(`/hospital-flowsheet/${id}`)}
                  onOpenRecord={pid => navigate(`/patients/${pid}/record`)}
                  onDischarge={handleDischarge}
                  onMarkCleaned={handleMarkCleaned}
                  cleaningHosp={cleaningHosp}
                  tvMode={tvMode}
                  now={now}
                />
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  }, [capacities, hospitalizationsWithConvenio, patients, activeSpecies, filterStatus, searchTerm, sortBy, tvMode, now, handleOpenAdmission, handleDischarge, handleMarkCleaned, cleaningPending, navigate]);

  // ── Render ───────────────────────────────────────────────────────────────────
  if (loading) return <Box sx={{ p:3, display:'flex', justifyContent:'center' }}><CircularProgress/></Box>;

  const updMins = lastUpdated ? Math.round((new Date()-lastUpdated)/60000) : null;
  const liveColor = updMins === null ? SUCCESS : updMins < 1 ? SUCCESS : updMins <= 3 ? AMBER : DANGER;

  return (
    <Box sx={{ bgcolor:'#F4F6F8', minHeight:'100vh', pb:4 }}>
      {/* ── HEADER KPIs ─── */}
      <Paper elevation={0} sx={{ px:3, pt:2, pb:1.5, borderBottom:'2px solid #E2E8F0', position:'sticky', top:0, zIndex:100, bgcolor:'white' }}>
        {/* Fila 1: título + búsqueda + controles */}
        <Stack direction="row" spacing={2} alignItems="center" mb={1.5} flexWrap="wrap">
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flex:'0 0 auto' }}>
            <LocalHospitalIcon sx={{ color: PRIMARY, fontSize: 28 }}/>
            <Typography variant="h6" fontWeight={800} color={PRIMARY}>Centro de Control</Typography>
          </Stack>

          <TextField size="small" placeholder="Buscar paciente, propietario, diagnóstico..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ color:'text.secondary', mr:0.5, fontSize:18 }}/> }}
            sx={{ flex:1, minWidth:200 }}/>

          <TextField size="small" select label="Estado" value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)} sx={{ minWidth:140 }}>
            <MenuItem value="todos">Todos</MenuItem>
            {Object.entries(CLINICAL_STATUS_CONFIG).map(([k,v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
          </TextField>

          <TextField size="small" select label="Ordenar" value={sortBy}
            onChange={e => setSortBy(e.target.value)} sx={{ minWidth:150 }}>
            <MenuItem value="estado">Estado (críticos primero)</MenuItem>
            <MenuItem value="proximoTto">Próximo tratamiento</MenuItem>
            <MenuItem value="diasInternado">Días internado</MenuItem>
            <MenuItem value="jaula">Nombre de jaula</MenuItem>
          </TextField>

          <ToggleButtonGroup size="small" value={viewMode} exclusive onChange={(_,v)=>v&&setViewMode(v)}>
            <ToggleButton value="grid"><Tooltip title="Vista grilla"><GridViewIcon sx={{ fontSize:18 }}/></Tooltip></ToggleButton>
            <ToggleButton value="list"><Tooltip title="Vista lista"><ListIcon sx={{ fontSize:18 }}/></Tooltip></ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title={tvMode ? 'Salir de modo TV' : 'Modo TV (pantalla completa)'}>
            <IconButton onClick={toggleTvMode} sx={{ color: tvMode ? AMBER : 'text.secondary' }}>
              {tvMode ? <FullscreenExitIcon/> : <FullscreenIcon/>}
            </IconButton>
          </Tooltip>

          <Tooltip title="Estado en tiempo real">
            <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
              <Box sx={{ width:8, height:8, borderRadius:'50%', bgcolor: liveColor }}/>
              <Typography variant="caption" color="text.secondary">
                {updMins === null ? 'Conectando…' : updMins < 1 ? 'En vivo' : `hace ${updMins} min`}
              </Typography>
            </Box>
          </Tooltip>
        </Stack>

        {/* Fila 2: KPI stat cards */}
        {!tvMode && (
          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            {[
              { label:'Internados', value: stats.totalInternados, color: PRIMARY },
              { label:'Ocupación', value: `${stats.ocupacionPct}%`, color: getOccupancyColor(stats.ocupacionPct) },
              { label:'Críticos', value: stats.criticos, color: stats.criticos > 0 ? DANGER : SUCCESS },
              { label:'Ttos atrasados', value: stats.overdue, color: stats.overdue > 0 ? DANGER : SUCCESS },
              { label:'Alta estimada hoy', value: stats.altaHoy, color: TEAL },
              { label:'Pendiente limpieza', value: stats.limpiezaPendiente, color: stats.limpiezaPendiente > 0 ? AMBER : SUCCESS },
            ].map(s => (
              <Paper key={s.label} elevation={0} sx={{ px:2, py:1, borderRadius:2, border:'1px solid #E2E8F0', textAlign:'center', minWidth:110 }}>
                <Typography variant="caption" color="text.secondary" display="block">{s.label}</Typography>
                <Typography variant="h5" fontWeight={800} sx={{ color: s.color }}>{s.value}</Typography>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>

      <Box sx={{ px:3, pt:2 }}>
        {error && <Alert severity="error" sx={{ mb:2 }}>{error}</Alert>}
        {stats.overdue > 0 && (
          <Alert severity="error" sx={{ mb:2, fontWeight: 600 }}>
            ⚠ {stats.overdue} paciente(s) con tratamiento(s) atrasado(s) — revisar hoja de tratamiento
          </Alert>
        )}
        {stats.limpiezaPendiente > 0 && (
          <Alert severity="warning" sx={{ mb:2 }}>
            🧹 {stats.limpiezaPendiente} jaula(s) pendiente(s) de limpieza
          </Alert>
        )}

        {/* Species tabs */}
        <Tabs value={tabValue} onChange={(_,v) => setTabValue(v)}
          sx={{ mb:2, borderBottom:'1px solid #E2E8F0', bgcolor:'white', borderRadius:2, px:1 }}>
          {speciesTabs.map((sp, i) => {
            const count = branchHospitalizations.filter(h => {
              if (sp === 'todos') return true;
              const s = (h.patientSpecies||'').toLowerCase();
              if (sp === 'canino') return s.includes('canino')||s.includes('perro');
              if (sp === 'felino') return s.includes('felino')||s.includes('gato');
              return true;
            }).length;
            const label = sp==='todos'?'Todos':sp.charAt(0).toUpperCase()+sp.slice(1);
            return (
              <Tab key={sp} label={<Badge badgeContent={count||null} color="primary">{label}</Badge>}/>
            );
          })}
        </Tabs>

        {/* Content: grid or list */}
        {viewMode === 'list' || capacities.length === 0 ? (
          <>
            {capacities.length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No se encontraron capacidades de hospital configuradas para esta sucursal. Se muestra la vista lista con los internados existentes.
              </Alert>
            )}
            <HospitalListView
              hospitalizations={hospitalizationsWithConvenio}
              patients={patients}
              onOpenFlowsheet={id => navigate(`/hospital-flowsheet/${id}`)}
              onOpenRecord={pid => navigate(`/patients/${pid}/record`)}
              onDischarge={handleDischarge}
              now={now}
            />
          </>
        ) : (
          <>
            {buildGroup(false)}
            {buildGroup(true)}
          </>
        )}
      </Box>

      {/* Dialogs */}
      <AdmissionStepperDialog
        open={admissionDialogOpen}
        onClose={handleCloseAdmission}
        selectedHospital={selectedHospital}
        selectedCage={selectedCage}
        patients={patients}
        diagnoses={diagnoses}
        users={users}
        savingAdmission={savingAdmission}
        admissionError={admissionError}
        onSubmit={handleSubmitAdmission}
        mode={newOrderMode ? 'order' : selectedOrder ? 'review' : 'admission'}
        initialPatient={dialogInitialPatient}
        initialFormData={selectedOrder ? selectedOrder.formData : null}
      />

      <PendingOrdersDialog
        open={pendingOrdersOpen}
        onClose={() => setPendingOrdersOpen(false)}
        pendingOrders={pendingOrders}
        onSelect={handleSelectPendingOrder}
        selectedCage={selectedCage}
        selectedHospital={selectedHospital}
        presupuestosById={presupuestosById}
      />

      <DischargeDialog
        open={dischargeDialogOpen}
        onClose={() => setDischargeDialogOpen(false)}
        hospitalization={selectedDischarge}
        onConfirm={handleConfirmDischarge}
        saving={savingDischarge}
      />

      <Snackbar open={snackbar.open} autoHideDuration={5000}
        onClose={() => setSnackbar(p=>({...p,open:false}))}
        anchorOrigin={{ vertical:'top', horizontal:'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(p=>({...p,open:false}))} sx={{ width:'100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Hospital;
